import { DESCUENTO_DEL_REFERIDO_PORCENTAJE } from '@mc/socios'
import { Check, Gift, Receipt, Sparkles } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useOutletContext, useSearchParams } from 'react-router'

import {
  facturacion as accionesDeFacturacion,
  useEspacio,
  useFacturacion,
  useHistorialDeCreditos,
  useMovimientos,
  useRefrescarFacturacion,
  useResumenDeCreditos,
  useSaldo,
} from '../api/consultas'
import { ErrorDeLaApi } from '../api/nucleo'
import type { CobroDeWixy, Facturacion, HistorialDeCreditos, Movimiento, PaqueteDeCreditos, PlanALaVenta, ResumenDeCreditos, Saldo, SuscripcionVigente } from '../api/tipos'
import { Escribenos } from '../componentes/Ayuda'
import { Aviso, Boton, clases, Esqueleto, Modal, Pastilla, Tarjeta, Titulo } from '../componentes/ui'
import { dolares } from '../socios/dinero'
import { creditosEnLlano, porcentajeDelCiclo } from './consumo'
import {
  accionDelPlan,
  capturarUnaVez,
  confirmarConEspera,
  esDireccionDePayPal,
  fechaLarga,
  impuestoAparte,
  leerVueltaDePayPal,
  listaDeModulos,
  mensajeDeCaptura,
  mensajeDeConfirmacion,
  movimientoEnLlano,
  mensajeDeVuelta,
  precioEnUnaLinea,
  precioVisible,
  sinParametrosDePayPal,
  textoDeAlcances,
  textoDeCreditos,
  textoDeSitios,
  type Mensaje,
} from './facturacion'

import type { Contexto } from './Modulo'

/** Un aviso arriba de la página; el de «volver a intentarlo» lleva su botón. */
type AvisoDePagina = Mensaje & { reintentar?: { texto: string; accion: () => void } }


const fecha = (iso: null | string | undefined) => (iso ? new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '')
const fechaCorta = (iso: null | string | undefined) => (iso ? new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')

const mensajeDe = (fallo: unknown, siNo: string) => (fallo instanceof ErrorDeLaApi ? fallo.message : siNo)

const Cifra = ({ etiqueta, valor, nota }: { etiqueta: string; nota?: string; valor: number }) => (
  <Tarjeta>
    <p className="text-[13px] text-tenue">{etiqueta}</p>
    <p className="mt-1 font-display text-[28px] leading-none font-semibold tracking-tight tabular-nums">{valor.toLocaleString('es')}</p>
    {nota && <p className="mt-2 text-xs text-tenue">{nota}</p>}
  </Tarjeta>
)

const Seccion = ({ id, titulo, nota, children }: { children: ReactNode; id: string; nota?: ReactNode; titulo: string }) => (
  <section aria-labelledby={`${id}-titulo`} className="mb-8" id={id}>
    <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 className="font-display text-lg font-semibold" id={`${id}-titulo`}>
        {titulo}
      </h2>
      {nota && <p className="text-xs text-tenue">{nota}</p>}
    </div>
    {children}
  </section>
)

/** La pastilla del descuento de socio: naranja de fondo, el texto en tinta para que se lea bien. */
const PastillaDeSocio = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex items-center gap-1.5 rounded-pastilla border border-naranja/35 bg-naranja-suave px-2 py-0.5 text-xs font-semibold whitespace-nowrap text-tinta">
    <Gift aria-hidden className="size-3.5 text-naranja" />
    {children}
  </span>
)

const Incluye = ({ children }: { children: ReactNode }) => (
  <li className="flex gap-2">
    <Check aria-hidden className="mt-0.5 size-4 shrink-0 text-violeta" />
    <span>{children}</span>
  </li>
)

const notaDelImpuesto = (f: Facturacion) =>
  impuestoAparte(f.impuesto) ? `En dólares (USD), más el ${f.impuesto.porcentaje.toLocaleString('es')} % de impuesto.` : 'En dólares (USD), con el impuesto incluido.'

/**
 * La vuelta de PayPal: `?paypal=…` en la dirección. Se quita de la barra en cuanto se lee
 * —así recargar no repite nada, y si el cliente se va a otra pantalla mientras se confirma,
 * nada lo trae de vuelta— y se cierra el pago: cobrar la orden de créditos (una vez) o
 * confirmar la suscripción (esperando a PayPal si tarda). Lo que pase, en un aviso arriba.
 */
const useVueltaDePayPal = (espacio: number, avisar: (aviso: AvisoDePagina) => void, activa: boolean) => {
  const [params, setParams] = useSearchParams()
  const refrescar = useRefrescarFacturacion(espacio)
  const consultaActual = params.toString()

  useEffect(() => {
    if (!activa) return
    const vuelta = leerVueltaDePayPal(new URLSearchParams(consultaActual))
    if (!vuelta) return
    setParams((antes) => sinParametrosDePayPal(antes), { replace: true })
    const acciones = accionesDeFacturacion(espacio)

    const procesar = async (): Promise<void> => {
      avisar(mensajeDeVuelta(vuelta))
      if (vuelta.tipo === 'creditos') {
        try {
          avisar(mensajeDeCaptura(await capturarUnaVez(vuelta.orden, acciones.capturar)))
        } catch (fallo) {
          avisar({ tono: 'error', texto: mensajeDe(fallo, 'No hemos podido confirmar la compra con PayPal.'), reintentar: { texto: 'Volver a intentarlo', accion: () => void procesar() } })
        } finally {
          refrescar()
        }
      }
      if (vuelta.tipo === 'suscripcion') {
        try {
          const estado = await confirmarConEspera(vuelta.suscripcion, async (id) => (await acciones.confirmar(id)).estado)
          const mensaje = mensajeDeConfirmacion(estado)
          avisar(estado === 'pendiente' ? { ...mensaje, reintentar: { texto: 'Comprobar otra vez', accion: () => void procesar() } } : mensaje)
        } catch (fallo) {
          avisar({ tono: 'error', texto: mensajeDe(fallo, 'No hemos podido confirmar la suscripción con PayPal.'), reintentar: { texto: 'Volver a intentarlo', accion: () => void procesar() } })
        } finally {
          refrescar()
        }
      }
    }
    void procesar()
    // Solo cuando cambia la dirección: `avisar`, `refrescar` y `setParams` no cambian lo que se hace.
  }, [activa, consultaActual, espacio]) // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * «Créditos y plan». El saldo sale del libro de movimientos, que es la verdad (§17):
 * `clientes.saldoDeCreditos` es una copia y si no cuadran, manda el libro. Y desde aquí
 * se paga a Wixy (§31): suscribirse a un plan y comprar créditos, con PayPal. Pagar es
 * una redirección a PayPal y una vuelta; el panel no carga ningún guion de PayPal.
 */
export const Creditos = () => {
  const { espacio, permisos } = useOutletContext<Contexto>()
  // Ver el saldo y el consumo es una capacidad (`creditos.ver`); pagar a Wixy —el plan, los
  // paquetes, cancelar— es del dueño de la cuenta, con su PayPal: el núcleo exige propietario
  // en `/api/plataforma/*` (§31), así que aquí se mira lo mismo.
  const administra = permisos.tiene('creditos.ver')
  const puedeComprar = permisos.esDueno
  const { data: cuenta } = useEspacio(espacio)
  const { data: saldo, isPending, error } = useSaldo(espacio)
  const { data: movimientos } = useMovimientos(espacio)
  // Por bolsa y por función, y cada llamada (plan del CMS dinámico §9): sin coste ni margen, que el núcleo no da.
  const { data: resumen } = useResumenDeCreditos(espacio)
  const [paginaDelHistorial, setPaginaDelHistorial] = useState(1)
  const { data: historial } = useHistorialDeCreditos(espacio, paginaDelHistorial)
  const { data: factura, error: errorDeFactura } = useFacturacion(espacio, puedeComprar)
  const refrescar = useRefrescarFacturacion(espacio)

  const [aviso, setAviso] = useState<AvisoDePagina | null>(null)
  /** Qué botón está abriendo PayPal (`plan:3`, `paquete:0`): los demás esperan. */
  const [pagando, setPagando] = useState<null | string>(null)
  const [falloDePago, setFalloDePago] = useState<null | { donde: 'paquetes' | 'planes'; texto: string }>(null)
  const [cancelando, setCancelando] = useState(false)

  useVueltaDePayPal(espacio, setAviso, puedeComprar)

  // Si el cliente vuelve atrás desde PayPal, el navegador puede devolver la página tal como
  // estaba —con el botón girando—: se suelta.
  useEffect(() => {
    const alVolver = (e: PageTransitionEvent) => {
      if (e.persisted) setPagando(null)
    }
    window.addEventListener('pageshow', alVolver)
    return () => window.removeEventListener('pageshow', alVolver)
  }, [])

  // Quien acaba de abrir su cuenta desde wixy.app eligiendo un plan de pago llega con `?pagar=<plan>`
  // (paginas/Registro.tsx): se abre PayPal sin que tenga que buscar el botón. Una sola vez, y solo si
  // el plan está a la venta y no es ya el suyo.
  const [parametros, setParametros] = useSearchParams()
  const pagoPedido = useRef(false)
  useEffect(() => {
    const slug = parametros.get('pagar')
    if (!slug || pagoPedido.current || !factura || !puedeComprar) return
    pagoPedido.current = true
    const siguientes = new URLSearchParams(parametros)
    siguientes.delete('pagar')
    setParametros(siguientes, { replace: true })
    const plan = factura.planes.find((p) => p.slug === slug && p.listo)
    if (!factura.cobrosActivos || !plan || plan.id === factura.planActual?.id) return
    // Lo mismo que el botón del plan (`irAPayPal`, más abajo: aquí todavía no existe).
    setPagando(`plan:${plan.id}`)
    accionesDeFacturacion(espacio)
      .suscribirse(plan.id)
      .then(({ aprobar }) => {
        if (!esDireccionDePayPal(aprobar)) throw new Error('PayPal no ha devuelto una dirección de pago válida.')
        window.location.assign(aprobar)
      })
      .catch((fallo: unknown) => {
        setFalloDePago({ donde: 'planes', texto: mensajeDe(fallo, 'No hemos podido abrir PayPal. Vuelve a intentarlo con el botón de tu plan.') })
        setPagando(null)
      })
  }, [factura, parametros, puedeComprar]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!administra) {
    if (permisos.cargando) return <Esqueleto />
    return (
      <>
        <Titulo>Créditos y plan</Titulo>
        <Aviso tono="info">Esto lo ve quien administra la cuenta. Si necesitas más créditos, pídeselos.</Aviso>
      </>
    )
  }
  if (error) return <Aviso tono="error">No hemos podido leer tu saldo. Vuelve a intentarlo en un momento.</Aviso>

  const acciones = accionesDeFacturacion(espacio)

  /** Pedir al núcleo la dirección de PayPal e ir. El botón sigue girando hasta que el navegador sale. */
  const irAPayPal = async (clave: string, donde: 'paquetes' | 'planes', empezar: () => Promise<{ aprobar: string }>) => {
    setPagando(clave)
    setFalloDePago(null)
    try {
      const { aprobar } = await empezar()
      if (!esDireccionDePayPal(aprobar)) {
        setFalloDePago({ donde, texto: 'PayPal no ha devuelto una dirección de pago válida. Vuelve a intentarlo en un momento.' })
        setPagando(null)
        return
      }
      window.location.assign(aprobar)
    } catch (fallo) {
      setFalloDePago({ donde, texto: mensajeDe(fallo, 'No hemos podido abrir PayPal. Vuelve a intentarlo en un momento.') })
      setPagando(null)
    }
  }

  const planDeLaCuenta = cuenta?.plan && typeof cuenta.plan === 'object' ? cuenta.plan : null
  const ofrecePagos = factura?.cobrosActivos === true
  // El cliente de una agencia revendedora (§36): sus créditos se los da su agencia, no se compran a Wixy.
  const deAgencia = factura?.deAgencia ?? null
  const sinPagos = !deAgencia && (Boolean(errorDeFactura) || factura?.cobrosActivos === false)
  const planesALaVenta = factura?.planes.filter((p) => p.listo) ?? []
  const nombreDelPlan = factura?.planActual?.nombre ?? planDeLaCuenta?.nombre ?? null
  // El cliente de una agencia puede llevar un tope propio (§36, R4): el resumen ya lo trae como sus créditos al mes.
  const creditosDelPlan = (deAgencia ? resumen?.plan?.creditosMensuales : undefined) ?? factura?.planes.find((p) => p.id === factura.planActual?.id)?.creditosMensuales ?? planDeLaCuenta?.creditosMensuales ?? null

  return (
    <>
      <Titulo descripcion="Tu plan, tus pagos y lo que gasta este espacio en IA —diseño, textos, SEO, imágenes, estructura e importación—: un solo saldo para todo.">Créditos y plan</Titulo>

      {aviso && (
        <div className="mb-6">
          <Aviso tono={aviso.tono}>
            {aviso.texto}
            {aviso.reintentar && (
              <button className="ml-2 font-medium text-violeta underline-offset-2 hover:underline" onClick={aviso.reintentar.accion} type="button">
                {aviso.reintentar.texto}
              </button>
            )}
          </Aviso>
        </div>
      )}

      {isPending || !saldo ? (
        <Tarjeta sinRelleno>
          <Esqueleto />
        </Tarjeta>
      ) : (
        <>
          {resumen?.bienvenida && resumen.bienvenida.quedan > 0 && (
            <div className="mb-4">
              <Aviso tono="ok">
                Te regalamos {creditosEnLlano(resumen.bienvenida.concedidos)} para empezar. Te quedan {creditosEnLlano(resumen.bienvenida.quedan)}: se gastan antes que los del plan y no caducan.
              </Aviso>
            </div>
          )}
          <div className={clases('mb-6 grid gap-4', (saldo.bienvenida ?? 0) > 0 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3')}>
            <Cifra etiqueta="Disponibles" valor={saldo.total} />
            {(saldo.bienvenida ?? 0) > 0 && <Cifra etiqueta="De bienvenida" nota="Se gastan primero. No caducan, pero no se reponen." valor={saldo.bienvenida ?? 0} />}
            <Cifra etiqueta="Del plan" nota="No se acumulan de un ciclo al siguiente." valor={saldo.concedidos} />
            {deAgencia ? (
              <Cifra etiqueta="Extra de tu proveedor" nota="No caducan con el mes." valor={saldo.comprados} />
            ) : (
              <Cifra etiqueta="Comprados" nota="Se acumulan hasta que se gastan." valor={saldo.comprados} />
            )}
          </div>

          {resumen && <GastoDelCiclo resumen={resumen} />}

          <TarjetaDelPlan
            alCancelar={() => setCancelando(true)}
            creditos={creditosDelPlan}
            nombre={nombreDelPlan}
            saldo={saldo}
            sinPagos={sinPagos}
            suscripcion={factura?.suscripcion ?? null}
          />

          {puedeComprar && !factura && !errorDeFactura && (
            <Tarjeta className="mb-8" sinRelleno>
              <Esqueleto filas={4} />
            </Tarjeta>
          )}
          {deAgencia && (
            <div className="mb-8">
              <Aviso tono="info">Tu plan y tus créditos de IA los gestiona {deAgencia}. Si necesitas más créditos o cambiar de plan, pídeselo.</Aviso>
            </div>
          )}
          {!puedeComprar && (
            <div className="mb-8">
              <Aviso tono="info">El plan, los pagos y la compra de créditos los gestiona el propietario de la cuenta. Si necesitas más créditos, pídeselos.</Aviso>
            </div>
          )}
          {errorDeFactura && (
            <div className="mb-8">
              <Aviso tono="error">No hemos podido leer tus planes y tus pagos. Vuelve a intentarlo en un momento.</Aviso>
            </div>
          )}

          {factura && ofrecePagos && planesALaVenta.length > 0 && (
            <Seccion id="planes" nota={notaDelImpuesto(factura)} titulo="Planes">
              {falloDePago?.donde === 'planes' && (
                <div className="mb-4">
                  <Aviso tono="error">{falloDePago.texto}</Aviso>
                </div>
              )}
              <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-4">
                {planesALaVenta.map((plan) => (
                  <TarjetaDePlan
                    actual={plan.id === factura.planActual?.id}
                    alElegir={() => void irAPayPal(`plan:${plan.id}`, 'planes', () => acciones.suscribirse(plan.id))}
                    factura={factura}
                    key={plan.id}
                    pagando={pagando}
                    plan={plan}
                  />
                ))}
              </div>
              <p className="mt-3 text-xs text-tenue">
                {factura.suscripcion && factura.suscripcion.estado !== 'cancelada'
                  ? 'Al cambiar de plan, el nuevo empieza cuando lo apruebas en PayPal y cancelamos el anterior para que no te cobre los dos.'
                  : 'Se paga cada mes con PayPal y se cancela cuando quieras, desde aquí.'}
              </p>
            </Seccion>
          )}

          {factura && ofrecePagos && factura.paquetes.length > 0 && (
            <Seccion id="comprar" nota={notaDelImpuesto(factura)} titulo="Comprar créditos">
              {falloDePago?.donde === 'paquetes' && (
                <div className="mb-4">
                  <Aviso tono="error">{falloDePago.texto}</Aviso>
                </div>
              )}
              <div className="grid grid-cols-[repeat(auto-fit,minmax(12.5rem,1fr))] gap-4">
                {factura.paquetes.map((paquete) => (
                  <TarjetaDePaquete
                    alComprar={() => void irAPayPal(`paquete:${paquete.indice}`, 'paquetes', () => acciones.comprar(paquete.indice))}
                    factura={factura}
                    key={paquete.indice}
                    pagando={pagando}
                    paquete={paquete}
                  />
                ))}
              </div>
              <div className="mt-3 flex flex-col gap-1.5">
                <p className="text-xs text-tenue">Los créditos comprados no caducan de un mes a otro.</p>
                {factura.descuentoDeSocio && (
                  <p className="flex items-center gap-1.5 text-[13px] text-tinta-2">
                    <Gift aria-hidden className="size-4 shrink-0 text-naranja" />
                    Tu primer pago lleva un {DESCUENTO_DEL_REFERIDO_PORCENTAJE} % de descuento, y ya está aplicado en estos precios.
                  </p>
                )}
              </div>
            </Seccion>
          )}

          {factura && (ofrecePagos || factura.cobros.length > 0) && (
            <Seccion id="pagos" titulo="Tus pagos">
              <TablaDePagos cobros={factura.cobros} />
            </Seccion>
          )}

          <Seccion id="historial" nota="Cada vez que la IA hace algo para ti, con lo que costó. Si algo no salió, no se cobra." titulo="En qué se han gastado">
            <TablaDelHistorial historial={historial} irA={setPaginaDelHistorial} />
          </Seccion>

          <Seccion id="movimientos" titulo="Movimientos">
            <TablaDeMovimientos movimientos={movimientos} />
          </Seccion>
        </>
      )}

      {cancelando && factura?.suscripcion && factura.suscripcion.estado !== 'cancelada' && (
        <ModalDeCancelar
          cancelar={async () => {
            const { hasta } = await acciones.cancelar()
            refrescar()
            const cuando = fechaLarga(hasta)
            setAviso({
              tono: 'ok',
              texto: cuando
                ? `Suscripción cancelada. Conservas el plan${nombreDelPlan ? ` ${nombreDelPlan}` : ''} hasta el ${cuando}; PayPal no te cobrará más.`
                : 'Suscripción cancelada. PayPal no te cobrará más.',
            })
          }}
          cerrar={() => setCancelando(false)}
          nombre={nombreDelPlan}
          suscripcion={factura.suscripcion}
        />
      )}
    </>
  )
}

// --- El plan de ahora ---------------------------------------------------------------------

const TarjetaDelPlan = ({
  nombre,
  creditos,
  saldo,
  suscripcion,
  sinPagos,
  alCancelar,
}: {
  alCancelar: () => void
  creditos: null | number
  nombre: null | string
  saldo: Saldo
  sinPagos: boolean
  suscripcion: null | SuscripcionVigente
}) => {
  const hasta = fechaLarga(suscripcion?.hasta)
  const suspendida = suscripcion?.estado === 'suspendida'
  const cancelada = suscripcion?.estado === 'cancelada'
  return (
    <Tarjeta className="mb-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[13px] text-tenue">Tu plan</p>
          <p className="mt-1 flex flex-wrap items-center gap-2">
            <span className="font-display text-xl font-semibold tracking-tight">{nombre ?? '—'}</span>
            {creditos ? <Pastilla clave="violeta">{creditos.toLocaleString('es')} créditos al mes</Pastilla> : null}
            {suscripcion?.estado === 'activa' && <Pastilla clave="publicado">Suscripción activa</Pastilla>}
            {suspendida && <Pastilla clave="construyendo">Cobro pendiente</Pastilla>}
            {cancelada && <Pastilla clave="neutra">Suscripción cancelada</Pastilla>}
          </p>
          {suscripcion?.estado === 'activa' && <p className="mt-1.5 text-[13px] text-tinta-2">{hasta ? `Se renueva el ${hasta}, con PayPal.` : 'Se paga cada mes con PayPal.'}</p>}
          {cancelada && (
            <p className="mt-1.5 text-[13px] text-tinta-2">
              {hasta ? `Sigues con este plan hasta el ${hasta}` : 'Sigues con este plan hasta el final de lo que pagaste'}; después pasa al plan gratuito, sin perder tus webs. Si lo reactivas,
              no se cobra nada antes de esa fecha.
            </p>
          )}
          <p className="mt-1 text-[13px] text-tenue">{saldo.cicloHasta ? `Los créditos del plan se renuevan el ${fecha(saldo.cicloHasta)}.` : 'Sin ciclo en marcha.'}</p>
        </div>
        {suscripcion && !cancelada ? (
          <Boton onClick={alCancelar} pequeno tipo="discreto">
            Cancelar la suscripción
          </Boton>
        ) : sinPagos ? (
          <p className="max-w-xs text-[13px] text-tenue">
            ¿Necesitas más créditos antes de que se renueven? Todavía no se compran desde aquí: <Escribenos contexto="mas-creditos">escríbenos</Escribenos> y te los
            añadimos.
          </p>
        ) : null}
      </div>
      {suspendida && (
        <div className="mt-4">
          <Aviso tono="aviso">PayPal no ha podido cobrar: revisa tu método de pago en PayPal.{hasta ? ` Mientras tanto conservas el plan hasta el ${hasta}.` : ''}</Aviso>
        </div>
      )}
    </Tarjeta>
  )
}

const ModalDeCancelar = ({ nombre, suscripcion, cancelar, cerrar }: { cancelar: () => Promise<void>; cerrar: () => void; nombre: null | string; suscripcion: SuscripcionVigente }) => {
  const [enCurso, setEnCurso] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  const hasta = fechaLarga(suscripcion.hasta)
  const confirmar = async () => {
    setEnCurso(true)
    setFallo(null)
    try {
      await cancelar()
      cerrar()
    } catch (e) {
      setFallo(mensajeDe(e, 'No hemos podido cancelar la suscripción. Vuelve a intentarlo en un momento.'))
      setEnCurso(false)
    }
  }
  return (
    <Modal
      ancho="max-w-md"
      cerrar={() => !enCurso && cerrar()}
      pie={
        <>
          <Boton disabled={enCurso} onClick={cerrar}>
            Mantenerla
          </Boton>
          <Boton cargando={enCurso} onClick={() => void confirmar()} tipo="peligro">
            Cancelar la suscripción
          </Boton>
        </>
      }
      titulo="¿Cancelar la suscripción?"
    >
      <div className="flex flex-col gap-3 text-sm text-tinta-2">
        <p>
          PayPal dejará de cobrarte cada mes. Conservas el plan{nombre ? ` ${nombre}` : ''} {hasta ? `hasta el ${hasta}` : 'hasta el final de lo que ya has pagado'}; después tu cuenta pasa al plan
          gratuito, sin perder tus webs.
        </p>
        <p>Los créditos que has comprado se quedan en tu saldo.</p>
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
      </div>
    </Modal>
  )
}

// --- Planes y paquetes --------------------------------------------------------------------

const TarjetaDePlan = ({ plan, factura, actual, pagando, alElegir }: { actual: boolean; alElegir: () => void; factura: Facturacion; pagando: null | string; plan: PlanALaVenta }) => {
  const clave = `plan:${plan.id}`
  const precio = precioVisible(plan.mensual, factura.impuesto)
  const conDescuento = factura.descuentoDeSocio && plan.primerMes < plan.mensual
  const accion = accionDelPlan(plan, factura.suscripcion)
  return (
    <div className={clases('flex flex-col rounded-caja border bg-papel p-5 shadow-suave', actual ? 'border-violeta ring-1 ring-violeta' : 'border-linea')}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display text-base font-semibold">{plan.nombre}</h3>
        {actual && <Pastilla clave="violeta">Tu plan</Pastilla>}
      </div>
      {conDescuento && (
        <div className="mt-3">
          <PastillaDeSocio>Primer mes {precioEnUnaLinea(plan.primerMes, factura.impuesto)}</PastillaDeSocio>
        </div>
      )}
      <p className="mt-3 flex flex-wrap items-baseline gap-x-1.5">
        <span className="font-display text-[34px] leading-none font-semibold tracking-tight tabular-nums">{precio.importe}</span>
        <span className="text-sm text-tenue">/mes{precio.masImpuesto ? ' + impuesto' : ''}</span>
      </p>
      {(precio.total || conDescuento) && (
        <p className="mt-1.5 text-xs text-tenue">
          {conDescuento ? 'A partir del segundo mes. ' : ''}
          {precio.total ? `Con el impuesto, ${precio.total} al mes.` : ''}
        </p>
      )}
      <ul className="mt-4 flex flex-col gap-2 border-t border-linea pt-4 text-[13px] text-tinta-2">
        {plan.creditosMensuales > 0 && <Incluye>{textoDeCreditos(plan.creditosMensuales)}</Incluye>}
        <Incluye>{textoDeSitios(plan.sitiosMaximos)}</Incluye>
        {plan.modulos.length > 0 && <Incluye>{listaDeModulos(plan.modulos)}</Incluye>}
        {textoDeAlcances(plan.alcances) && <Incluye>{textoDeAlcances(plan.alcances)}</Incluye>}
      </ul>
      <div className="mt-auto pt-5">
        <Boton
          cargando={pagando === clave}
          className="w-full"
          disabled={accion.deshabilitado || (pagando !== null && pagando !== clave)}
          icono={accion.deshabilitado ? <Check /> : undefined}
          onClick={alElegir}
          tipo={accion.deshabilitado ? 'secundario' : 'primario'}
        >
          {accion.texto}
        </Boton>
        {accion.nota && <p className="mt-2 text-xs text-tenue">{accion.nota}</p>}
      </div>
    </div>
  )
}

const TarjetaDePaquete = ({ paquete, factura, pagando, alComprar }: { alComprar: () => void; factura: Facturacion; pagando: null | string; paquete: PaqueteDeCreditos }) => {
  const clave = `paquete:${paquete.indice}`
  const precio = precioVisible(paquete.centimos, factura.impuesto)
  return (
    <div className={clases('flex flex-col rounded-caja border p-5 shadow-suave', paquete.destacado ? 'border-violeta-borde bg-violeta-suave/40' : 'border-linea bg-papel')}>
      <div className="flex min-h-8 items-center justify-between gap-2">
        <span aria-hidden className="flex size-8 items-center justify-center rounded-full bg-violeta-suave text-violeta">
          <Sparkles className="size-4" />
        </span>
        {paquete.destacado && <Pastilla clave="violeta">Destacado</Pastilla>}
      </div>
      <p className="mt-4 font-display text-[30px] leading-none font-semibold tracking-tight tabular-nums">{paquete.creditos.toLocaleString('es')}</p>
      <p className="mt-1 text-[13px] text-tenue">créditos</p>
      <p className="mt-4 text-lg font-semibold tabular-nums">
        {precio.importe}
        {precio.masImpuesto && <span className="ml-1 text-xs font-normal text-tenue">+ impuesto</span>}
      </p>
      {precio.total && <p className="text-xs text-tenue">Con el impuesto, {precio.total}.</p>}
      <div className="mt-auto pt-5">
        <Boton cargando={pagando === clave} className="w-full" disabled={pagando !== null && pagando !== clave} onClick={alComprar} tipo="primario">
          Comprar con PayPal
        </Boton>
      </div>
    </div>
  )
}

// --- Lo que se gasta ----------------------------------------------------------------------

/** Lo gastado en el ciclo en curso, con una barra contra los créditos del plan, y en qué función. */
const GastoDelCiclo = ({ resumen }: { resumen: ResumenDeCreditos }) => {
  const delPlan = resumen.plan?.creditosMensuales ?? 0
  const porcentaje = porcentajeDelCiclo(resumen.periodo.consumidos, delPlan)
  const usadas = resumen.desglose.filter((d) => d.llamadas > 0)
  const desde = fechaCorta(resumen.periodo.desde)
  return (
    <Tarjeta className="mb-8">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-base font-semibold">Este ciclo</h2>
        <p className="text-xs text-tenue">Desde el {desde}</p>
      </div>
      <p className="mt-2 text-sm text-tinta-2">
        Has gastado <span className="font-semibold text-tinta tabular-nums">{creditosEnLlano(resumen.periodo.consumidos)}</span>
        {delPlan > 0 ? ` de los ${delPlan.toLocaleString('es')} que trae tu plan cada mes.` : '.'}
      </p>
      {porcentaje !== null && (
        <div
          aria-label={`${porcentaje} % de los créditos del plan`}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={porcentaje}
          className="mt-3 h-2 overflow-hidden rounded-full bg-papel-3"
          role="progressbar"
        >
          <div className={clases('h-full rounded-full', porcentaje >= 100 ? 'bg-error' : porcentaje >= 80 ? 'bg-naranja' : 'bg-violeta')} style={{ width: `${porcentaje}%` }} />
        </div>
      )}
      {usadas.length === 0 ? (
        <p className="mt-4 text-[13px] text-tenue">Todavía no has usado la IA en este ciclo.</p>
      ) : (
        <ul className="mt-4 flex flex-col divide-y divide-linea border-t border-linea text-sm">
          {usadas.map((d) => (
            <li className="flex items-baseline justify-between gap-3 py-2" key={d.tipo}>
              <span className="font-medium">{d.funcion}</span>
              <span className="text-tinta-2 tabular-nums">
                {creditosEnLlano(d.creditos)}
                <span className="ml-2 text-xs text-tenue">
                  {d.llamadas.toLocaleString('es')} {d.llamadas === 1 ? 'vez' : 'veces'}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Tarjeta>
  )
}

const TablaDelHistorial = ({ historial, irA }: { historial: HistorialDeCreditos | undefined; irA: (pagina: number) => void }) => (
  <Tarjeta sinRelleno>
    {!historial ? (
      <Esqueleto filas={4} />
    ) : historial.filas.length === 0 ? (
      <p className="p-6 text-center text-sm text-tenue">Todavía no has usado la IA.</p>
    ) : (
      <>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-linea bg-papel-2 text-left text-xs text-tenue">
                <th className="px-4 py-2.5 font-medium">Cuándo</th>
                <th className="px-4 py-2.5 font-medium">Para qué</th>
                {/* El modelo no le cabe al móvil; lo que importa ahí es para qué y cuánto. */}
                <th className="hidden px-4 py-2.5 font-medium sm:table-cell">Modelo</th>
                <th className="px-4 py-2.5 text-right font-medium">Créditos</th>
                <th className="px-4 py-2.5 font-medium">Resultado</th>
              </tr>
            </thead>
            <tbody>
              {historial.filas.map((f) => (
                <tr className="border-b border-linea last:border-b-0" key={f.id}>
                  <td className="px-4 py-3 whitespace-nowrap text-tenue tabular-nums">{fecha(f.fecha)}</td>
                  <td className="px-4 py-3 font-medium">{f.funcion}</td>
                  <td className="hidden px-4 py-3 whitespace-nowrap text-tinta-2 sm:table-cell">{f.modelo ?? '—'}</td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">{f.creditos.toLocaleString('es')}</td>
                  <td className="px-4 py-3 text-tinta-2">{f.estado}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {historial.paginas > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-linea px-4 py-2.5 text-[13px] text-tenue">
            <span>
              Página {historial.pagina} de {historial.paginas}
            </span>
            <span className="flex gap-2">
              <Boton disabled={historial.pagina <= 1} onClick={() => irA(historial.pagina - 1)} pequeno>
                Más nuevas
              </Boton>
              <Boton disabled={historial.pagina >= historial.paginas} onClick={() => irA(historial.pagina + 1)} pequeno>
                Más antiguas
              </Boton>
            </span>
          </div>
        )}
      </>
    )}
  </Tarjeta>
)

// --- Tablas -------------------------------------------------------------------------------

const TablaDePagos = ({ cobros }: { cobros: CobroDeWixy[] }) => (
  <Tarjeta sinRelleno>
    {!cobros.length ? (
      <div className="flex flex-col items-center px-6 py-10 text-center">
        <span aria-hidden className="mb-3 flex size-10 items-center justify-center rounded-full bg-violeta-suave text-violeta">
          <Receipt className="size-5" />
        </span>
        <p className="text-sm font-medium">Todavía no has pagado nada</p>
        <p className="mt-1 max-w-sm text-[13px] text-tenue">Cuando te suscribas a un plan o compres créditos, cada pago aparecerá aquí con su fecha y su importe.</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-linea bg-papel-2 text-left text-xs text-tenue">
              <th className="px-4 py-2.5 font-medium">Fecha</th>
              <th className="px-4 py-2.5 font-medium">Concepto</th>
              <th className="px-4 py-2.5 text-right font-medium">Importe</th>
              <th className="px-4 py-2.5 font-medium">Estado</th>
            </tr>
          </thead>
          <tbody>
            {cobros.map((c) => (
              <tr className="border-b border-linea last:border-b-0" key={c.id}>
                <td className="px-4 py-3 whitespace-nowrap text-tenue tabular-nums">{fechaCorta(c.pagadoEn)}</td>
                <td className="px-4 py-3 font-medium">{c.concepto}</td>
                <td className={clases('px-4 py-3 text-right whitespace-nowrap tabular-nums', c.devuelto ? 'text-tenue line-through' : 'font-medium')}>{dolares(c.importe)}</td>
                <td className="px-4 py-3">{c.devuelto ? <Pastilla clave="neutra">Devuelto</Pastilla> : <Pastilla clave="publicado">Pagado</Pastilla>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Tarjeta>
)

const TablaDeMovimientos = ({ movimientos }: { movimientos: Movimiento[] | undefined }) => (
  <Tarjeta sinRelleno>
    {!movimientos?.length ? (
      <p className="p-6 text-center text-sm text-tenue">Todavía no hay movimientos.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-linea bg-papel-2 text-left text-xs text-tenue">
              <th className="px-4 py-2.5 font-medium">Qué</th>
              <th className="hidden px-4 py-2.5 font-medium sm:table-cell">Motivo</th>
              <th className="px-4 py-2.5 text-right font-medium">Créditos</th>
              <th className="hidden px-4 py-2.5 text-right font-medium sm:table-cell">Saldo</th>
              <th className="px-4 py-2.5 font-medium">Cuándo</th>
            </tr>
          </thead>
          <tbody>
            {movimientos.map((m) => (
              <tr className="border-b border-linea last:border-b-0" key={m.id}>
                <td className="px-4 py-3">
                  <span className="font-medium whitespace-nowrap">{movimientoEnLlano(m).que}</span>
                  {/* En el móvil el motivo va debajo, no en una columna de una palabra por línea. */}
                  <span className="mt-0.5 block text-xs text-tinta-2 sm:hidden">{movimientoEnLlano(m).motivo}</span>
                </td>
                <td className="hidden px-4 py-3 text-tinta-2 sm:table-cell">{movimientoEnLlano(m).motivo}</td>
                <td className={clases('px-4 py-3 text-right font-medium tabular-nums', m.cantidad < 0 ? 'text-error' : 'text-ok')}>
                  {m.cantidad > 0 ? '+' : ''}
                  {m.cantidad.toLocaleString('es')}
                </td>
                <td className="hidden px-4 py-3 text-right text-tinta-2 tabular-nums sm:table-cell">{m.saldoResultante.toLocaleString('es')}</td>
                <td className="px-4 py-3 whitespace-nowrap text-tenue tabular-nums">{fecha(m.fecha)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Tarjeta>
)
